import React, { useEffect, useState, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TreeView from "@mui/lab/TreeView";
import TreeItem from "@mui/lab/TreeItem";
import LockIcon from "@mui/icons-material/Lock";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

export function ArticleLeftBar({ reloadArticle, setReload, selectArticle }) {
  const [expanded, setExpanded] = useState([]);
  const [selected, setSelected] = useState([]);
  const [articleInEdition, setArticleInEdition] = useState([]);
  const [publishArticle, setPublishArticle] = useState([]);
  const [articlesAlreadyPublished, setArticlesAlreadyPublished] = useState([]);
  const [articlesByYearCache, setArticlesByYearCache] = useState({});
  const [selectedYear, setSelectedYear] = useState(null);

  const { t, i18n } = useTranslation(["Articles"]);
  const userState = useSelector((state) => state.user);

  const currentYear = new Date().getFullYear();
  const yearList = [
    currentYear - 3,
    currentYear - 2,
    currentYear - 1,
    currentYear,
  ];

  const debounceTimer = useRef(null);

  // ---------- FETCH UTILS ----------
  const fetchArticles = useCallback(
    async (endpoint, setState) => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/article/${endpoint}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userState.token}`,
            },
          }
        );
        if (!response.ok) return;
        const data = await response.json();
        setState(data);
      } catch (error) {
        console.error(`Failed to fetch ${endpoint}`, error);
      }
    },
    [userState.token]
  );

  const fetchArticlesAlreadyPublishedByYear = useCallback(
    async (year) => {
      if (articlesByYearCache[year]) {
        setArticlesAlreadyPublished(articlesByYearCache[year]);
        return;
      }
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/article/GetArticlesAlreadyPublishedByYear/${year}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userState.token}`,
            },
          }
        );
        if (!response.ok) return;
        const data = await response.json();
        setArticlesByYearCache((prev) => ({ ...prev, [year]: data }));
        setArticlesAlreadyPublished(data);
      } catch (error) {
        console.error(`Failed to fetch for year ${year}`, error);
      }
    },
    [userState.token, articlesByYearCache]
  );

  // ---------- EFFECTS ----------
  useEffect(() => {
    fetchArticles("GetPublishedArticles", setPublishArticle);
    fetchArticles("GetArticlesInEdition", setArticleInEdition);
  }, [fetchArticles]);

  useEffect(() => {
    if (reloadArticle) {
      fetchArticles("GetPublishedArticles", setPublishArticle);
      fetchArticles("GetArticlesInEdition", setArticleInEdition);
      setReload(false);
    }
  }, [reloadArticle, fetchArticles, setReload]);

  useEffect(() => {
    if (selectedYear) {
      fetchArticlesAlreadyPublishedByYear(selectedYear);
    }
  }, [selectedYear, fetchArticlesAlreadyPublishedByYear]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // ---------- HANDLERS ----------
  const handleToggle = (event, nodeIds) => setExpanded(nodeIds);

  const handleSelect = (event, nodeIds) => {
    setSelected(nodeIds);
    handleYearSelect(nodeIds);
  };

  const handleYearSelect = (nodeId) => {
    const id = Array.isArray(nodeId) ? nodeId[0] : nodeId;
    if (typeof id === "string" && id.startsWith("year-")) {
      const year = parseInt(id.replace("year-", ""));
      if (!isNaN(year) && yearList.includes(year)) {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => setSelectedYear(year), 300);
      }
    }
  };

  // ---------- RENDER ----------
  const renderArticleLabel = (article) => {
    const lang = i18n.language;
    const title = lang === "FR" ? article.titleFr : article.titleEn;
    return article.idEmployeLock ? (
      <Tooltip title={`Lock by ${article.employeLock}`} placement="right" arrow>
        <span>
          {title} <LockIcon />
        </span>
      </Tooltip>
    ) : (
      <span>{title}</span>
    );
  };

  const renderTreeItems = (articles, label, nodeId) => {
    const articlesArray = Array.isArray(articles) ? articles : [];
    return (
      <TreeItem nodeId={nodeId} label={label}>
        {articlesArray.map((article) => (
          <TreeItem
            key={article.id}
            nodeId={article.id.toString()}
            label={renderArticleLabel(article)}
            onClick={() => selectArticle(article)}
          />
        ))}
      </TreeItem>
    );
  };

  return (
    <div className="EdditionInProgress__Main">
      <div className="EdditionInProgress__Title">
        <h3>{t("Articles")}</h3>
      </div>
      <div className="EdditionInProgress__Treeview">
        <Box sx={{ height: 270, flexGrow: 1, maxWidth: 400, overflowY: "auto" }}>
          <TreeView
            aria-label="controlled"
            defaultCollapseIcon={<ExpandMoreIcon />}
            defaultExpandIcon={<ChevronRightIcon />}
            expanded={expanded}
            selected={selected}
            onNodeToggle={handleToggle}
            onNodeSelect={handleSelect}
            multiSelect
          >
            {renderTreeItems(articleInEdition, t("InEddition"), "24500")}
            {renderTreeItems(publishArticle, t("Published"), "26000")}
            <TreeItem nodeId="40000" label={t("AlreadyPublished")}>
              {yearList.map((year) => (
                <TreeItem key={year} nodeId={`year-${year}`} label={year}>
                  {selectedYear === year
                    ? renderTreeItems(
                        articlesAlreadyPublished,
                        t("AlreadyPublished"),
                        `published-${year}`
                      )
                    : null}
                </TreeItem>
              ))}
            </TreeItem>
          </TreeView>
        </Box>
      </div>
    </div>
  );
}

export default ArticleLeftBar;
